import type { Prisma, PrismaClient } from '@prisma/client';
import { Router, type Request } from 'express';
import { z } from 'zod';
import { audit } from '../../core/audit.js';
import type { Config } from '../../core/config.js';
import { HttpError } from '../../core/errors.js';
import { asyncHandler, clientIp, pageParams, parse } from '../../core/http.js';
import { idempotent } from '../../core/idempotency.js';
import { authenticate, requirePermission } from '../auth/auth.middleware.js';
import { csvLine } from '../csv/csv.parser.js';
import { customFieldError } from '../parties/custom-fields.js';
import {
  itemInclude,
  itemInputSchema,
  itemReferenceErrors,
  itemView,
  saveItem,
} from './catalog.service.js';

interface Deps {
  db: PrismaClient;
  config: Config;
}

export function createCatalogRouter({ db, config }: Deps) {
  const router = Router();
  router.use('/items', authenticate({ db, config }), idempotent(db));
  const actor = (req: Request) => ({
    companyId: req.auth!.companyId,
    userId: req.auth!.userId,
    ip: clientIp(req),
  });

  function listWhere(req: Request): Prisma.ItemWhereInput {
    const q = req.query as Record<string, string | undefined>;
    const search = q.search?.trim();
    return {
      companyId: req.auth!.companyId,
      ...(q.type ? { type: q.type } : {}),
      ...(q.active === 'false' ? { active: false } : q.active === 'all' ? {} : { active: true }),
      ...(q.needsReview === 'true' ? { needsReview: true } : {}),
      ...(search ? { OR: [{ name: { contains: search } }, { sku: { contains: search } }] } : {}),
    };
  }

  router.get(
    '/items',
    requirePermission('sales.item.view'),
    asyncHandler(async (req, res) => {
      const { page, pageSize, skip, take } = pageParams(req.query as Record<string, unknown>);
      const where = listWhere(req);
      const [total, rows] = await Promise.all([
        db.item.count({ where }),
        db.item.findMany({ where, include: itemInclude, orderBy: { name: 'asc' }, skip, take }),
      ]);
      res.json({ items: rows.map(itemView), total, page, pageSize });
    }),
  );

  router.get(
    '/items/search',
    requirePermission('sales.item.view'),
    asyncHandler(async (req, res) => {
      const q = String(req.query.q ?? '').trim();
      const rows = await db.item.findMany({
        where: {
          companyId: req.auth!.companyId,
          active: true,
          ...(q ? { OR: [{ name: { contains: q } }, { sku: { contains: q } }] } : {}),
        },
        include: itemInclude,
        orderBy: { name: 'asc' },
        take: 20,
      });
      res.json({
        items: rows.map((i) => {
          const ugx = i.prices.find((p) => p.currency === 'UGX');
          return {
            id: i.id,
            label: i.name,
            description:
              [
                i.sku,
                ugx ? `UGX ${Number(ugx.price).toLocaleString('en-US')}` : null,
                i.needsReview ? 'Incomplete' : null,
              ]
                .filter(Boolean)
                .join(' · ') || undefined,
          };
        }),
      });
    }),
  );

  router.get(
    '/items/export.csv',
    requirePermission('sales.item.export'),
    asyncHandler(async (req, res) => {
      const where = listWhere(req);
      res.setHeader('Content-Type', 'text/csv; charset=utf-8');
      res.setHeader('Content-Disposition', 'attachment; filename="items.csv"');
      res.write(
        '﻿' +
          csvLine([
            'name',
            'sku',
            'type',
            'description',
            'unit',
            'vat_code',
            'revenue_account_key',
            'cost_price',
            'price_ugx',
            'price_usd',
            'active',
          ]) +
          '\r\n',
      );
      let count = 0;
      let cursor = 0;
      for (;;) {
        const rows = await db.item.findMany({
          where,
          include: itemInclude,
          orderBy: { id: 'asc' },
          take: 500,
          ...(cursor ? { cursor: { id: cursor }, skip: 1 } : {}),
        });
        if (rows.length === 0) break;
        for (const i of rows) {
          const price = (c: string) =>
            i.prices.find((p) => p.currency === c)?.price.toFixed(2) ?? '';
          res.write(
            csvLine([
              i.name,
              i.sku,
              i.type,
              i.description,
              i.unit,
              i.vatCode,
              i.revenueAccountKey,
              i.costPrice?.toFixed(2) ?? '',
              price('UGX'),
              price('USD'),
              i.active ? 'yes' : 'no',
            ]) + '\r\n',
          );
          count += 1;
        }
        cursor = rows[rows.length - 1]!.id;
      }
      await db.exportLog.create({
        data: {
          companyId: req.auth!.companyId,
          userId: req.auth!.userId,
          entity: 'items',
          filters: req.query as Prisma.InputJsonValue,
          rowCount: count,
        },
      });
      await audit(db, actor(req), {
        action: 'export.csv',
        entity: 'items',
        after: { rows: count },
      });
      res.end(); // logged first so a client that reads the whole file can rely on the audit entry
    }),
  );

  router.get(
    '/items/:id',
    requirePermission('sales.item.view'),
    asyncHandler(async (req, res) => {
      const item = await db.item.findFirst({
        where: { id: Number(req.params.id), companyId: req.auth!.companyId },
        include: itemInclude,
      });
      if (!item) throw new HttpError(404, 'NOT_FOUND', 'Item not found');
      res.json(itemView(item));
    }),
  );

  async function checkedInput(req: Request, input: Parameters<typeof itemReferenceErrors>[2]) {
    const errors = await itemReferenceErrors(db, req.auth!.companyId, input);
    if (errors.length) throw new HttpError(400, 'VALIDATION_ERROR', errors.join('; '));
  }

  router.post(
    '/items',
    requirePermission('sales.item.create'),
    asyncHandler(async (req, res) => {
      const input = parse(itemInputSchema, req.body);
      await checkedInput(req, input);
      const defs = await db.customFieldDef.findMany({
        where: { companyId: req.auth!.companyId, entity: 'item' },
      });
      const problem = customFieldError(defs, input.customFields);
      if (problem) throw new HttpError(400, 'INVALID_CUSTOM_FIELD', problem);
      if (
        input.sku &&
        (await db.item.findUnique({
          where: { companyId_sku: { companyId: req.auth!.companyId, sku: input.sku } },
        }))
      ) {
        throw new HttpError(409, 'SKU_TAKEN', `SKU ${input.sku} is already used`);
      }
      const item = await db.$transaction(async (tx) => {
        const created = await saveItem(tx, {
          companyId: req.auth!.companyId,
          userId: req.auth!.userId,
          input,
        });
        await audit(tx, actor(req), {
          action: 'item.created',
          entity: 'item',
          entityId: created.id,
          after: itemView(created),
        });
        return created;
      });
      res.status(201).json(itemView(item));
    }),
  );

  /** Create in place from a document line: name, type, price, VAT category. Flagged Incomplete. */
  router.post(
    '/items/quick',
    requirePermission('sales.item.create'),
    asyncHandler(async (req, res) => {
      const body = parse(
        z.object({
          name: z.string().trim().min(2).max(200),
          type: z
            .enum(['service', 'product', 'domain', 'hosting', 'subscription'])
            .default('service'),
          price: z
            .union([z.string(), z.number()])
            .transform((v) => String(v))
            .refine((v) => /^\d{1,15}(\.\d{1,4})?$/.test(v), 'Enter a valid price')
            .optional(),
          vatCode: z.string().trim().default('VAT_STD'),
        }),
        req.body,
      );
      await checkedInput(req, { vatCode: body.vatCode, prices: [] });
      const item = await db.$transaction(async (tx) => {
        const created = await saveItem(tx, {
          companyId: req.auth!.companyId,
          userId: req.auth!.userId,
          needsReview: true,
          input: {
            name: body.name,
            type: body.type,
            vatCode: body.vatCode,
            prices: body.price ? [{ currency: 'UGX', price: body.price }] : [],
          },
        });
        await audit(tx, actor(req), {
          action: 'item.created_in_place',
          entity: 'item',
          entityId: created.id,
          after: { name: created.name },
        });
        return created;
      });
      res.status(201).json({ id: item.id, label: item.name, description: 'Incomplete' });
    }),
  );

  router.patch(
    '/items/:id',
    requirePermission('sales.item.edit'),
    asyncHandler(async (req, res) => {
      const id = Number(req.params.id);
      const input = parse(itemInputSchema.partial(), req.body);
      const before = await db.item.findFirst({
        where: { id, companyId: req.auth!.companyId },
        include: itemInclude,
      });
      if (!before) throw new HttpError(404, 'NOT_FOUND', 'Item not found');
      await checkedInput(req, {
        vatCode: input.vatCode ?? before.vatCode,
        prices: input.prices ?? [],
      });
      if (
        input.sku &&
        input.sku !== before.sku &&
        (await db.item.findUnique({
          where: { companyId_sku: { companyId: before.companyId, sku: input.sku } },
        }))
      ) {
        throw new HttpError(409, 'SKU_TAKEN', `SKU ${input.sku} is already used`);
      }
      const after = await db.$transaction(async (tx) => {
        const updated = await saveItem(tx, {
          companyId: before.companyId,
          userId: req.auth!.userId,
          id,
          input,
        });
        await audit(tx, actor(req), {
          action: 'item.updated',
          entity: 'item',
          entityId: id,
          before: itemView(before),
          after: itemView(updated),
        });
        return updated;
      });
      res.json(itemView(after));
    }),
  );

  router.post(
    '/items/:id/review-complete',
    requirePermission('sales.item.edit'),
    asyncHandler(async (req, res) => {
      const id = Number(req.params.id);
      const before = await db.item.findFirst({ where: { id, companyId: req.auth!.companyId } });
      if (!before) throw new HttpError(404, 'NOT_FOUND', 'Item not found');
      const updated = await db.item.update({
        where: { id },
        data: { needsReview: false },
        include: itemInclude,
      });
      await audit(db, actor(req), {
        action: 'item.review_completed',
        entity: 'item',
        entityId: id,
      });
      res.json(itemView(updated));
    }),
  );

  return router;
}
